import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import QRCodeStyling from '@plasticprintersinc/pp-qr-code';
import { useInView } from 'react-intersection-observer';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTypeHeaderIcon } from '../../../../utils/hooks/useTypeHeaderIcon';
import styles from './QRCard.module.css';

export default function QRCard({QR, setParent}) {

    const qrRef = useRef(null);

    const { ref, inView } = useInView({
        root: null,
        rootMargin: '0px 0px -25px',
        threshold: .25
    });

    const options = useMemo(() => QR?.data?.qr_style, [QR?.data?.qr_style]);
    const [ code, setCode ] = useState(inView ? new QRCodeStyling(options) : null);
    const { icon } = useTypeHeaderIcon(QR?.data?.qr_type);
    const [ confirm, setConfirm ] = useState(false);

    useEffect(() => {
        if (!inView || !qrRef.current || !options) return;

        if (!code) {
            const newCode = new QRCodeStyling(options);
            setCode(newCode);
            newCode.append(qrRef.current);
        } else {
            code.update(options);
            // this checks if canvas is already appended
            if (!qrRef.current.hasChildNodes()) {
                code.append(qrRef.current);
            }
        }

        return () => {
            if (code && qrRef.current) {
                const canvas = qrRef.current.querySelector('canvas');
                if (canvas) {
                    qrRef.current.removeChild(canvas);
                }
            }
        }
    },[inView, code, options])

    useEffect(() => {
        if (confirm) {
            const timeout = setTimeout(() => {
                setConfirm(false);
            },2000);

            return () => clearTimeout(timeout);
        };
    },[confirm]);

    const renderTooltip = useCallback((props) => (
        <Tooltip id={`tooltip-${QR.id}`} {...props} className={styles.tip}>
            <div>Name: <span>{QR.data.nickname}</span></div>
            <div>Type: <span>{QR.data.qr_type.charAt(0).toUpperCase() + QR.data.qr_type.slice(1)} <i className={icon} /></span></div>
        </Tooltip>
    ), [QR.id, QR.data.nickname, QR.data.qr_type, icon]);

    const handleSelectClick = useCallback(() => {
        setConfirm(true);
    }, []);

    const handleConfirmClick = useCallback(() => {
        setParent({ obj: QR, selected: true });
    }, [QR, setParent]);

    return (
        <div className={styles.card} id={QR.id} ref={ref}>
            <OverlayTrigger 
                placement="top"
                overlay={renderTooltip}
            >
                <div className={styles.name}>{QR.data.nickname}</div>
            </OverlayTrigger>
            <div className={styles.qr} ref={qrRef}>
                {!code && inView ? 
                    <i className='fa-duotone fa-spinner fa-spin' style={{fontSize: '2rem'}} /> 
                    : <></>
                }
            </div>
            <div className={styles.btns}>
                <button 
                    className={confirm ? `${styles.selectBtn} ${styles.hide}` : styles.selectBtn} 
                    onClick={handleSelectClick}>Select
                </button>
                <button 
                    className={confirm ? `${styles.confirmBtn} ${styles.show}` : styles.confirmBtn} 
                    onClick={handleConfirmClick}>Confirm
                </button>
            </div>
        </div>
    );
};